import Route from '@ember/routing/route';
import { getOwner } from '@ember/owner';
import { generateHomePageStructuredData } from 'madow-tech/utils/schema-org';
import type MetaService from 'madow-tech/services/meta';

export default class EnRoute extends Route {
  model() {
    // Get intl service
    const owner = getOwner(this);
    if (!owner) return {};

    const intl = owner.lookup('service:intl');
    if (!intl) return {};

    // Set locale to English
    intl.setLocale(['en']);

    // Persist locale preference in localStorage (preferred) or cookie (fallback)
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem('locale', 'en');
      } catch (e) {
        // localStorage might be disabled, fall back to cookie
        if (typeof document !== 'undefined') {
          document.cookie = `locale=en;path=/;max-age=31536000`;
        }
      }
    } else if (typeof document !== 'undefined') {
      document.cookie = `locale=en;path=/;max-age=31536000`;
    }

    // Set up SEO metadata
    this.setupSEO(owner);

    return {};
  }

  private setupSEO(owner: ReturnType<typeof getOwner>) {
    const meta = owner?.lookup('service:meta') as MetaService | undefined;
    if (!meta) return;

    // Reset previous meta
    meta.reset();

    // Set basic metadata
    meta.title = "Let's build scalable web & mobile apps!";
    meta.description =
      'A young team of experienced developers and designers building web platforms, mobile apps, and digital products that actually work. From Argentina to the world.';
    meta.canonicalUrl = 'https://madow.tech/en';

    // Set hreflang alternate languages
    meta.alternateLanguages = [
      { lang: 'en', url: 'https://madow.tech/en' },
      { lang: 'es', url: 'https://madow.tech/es' },
    ];

    // Set custom meta tags
    meta.setTags([
      {
        name: 'keywords',
        content:
          'software development, web development, mobile apps, team augmentation, TypeScript, Ember.js, React, NestJS, Argentina, nearshore, LATAM developers',
      },
      { property: 'og:locale', content: 'en_US' },
      { property: 'og:locale:alternate', content: 'es_AR' },
      { name: 'language', content: 'English' },
      { name: 'geo.region', content: 'AR-X' },
      { name: 'geo.placename', content: 'Córdoba' },
    ]);

    // Add structured data
    meta.scriptTags = [
      {
        type: 'application/ld+json',
        id: 'structured-data-main',
        content: generateHomePageStructuredData('en'),
      },
    ];

    // Render meta to header
    meta.renderMetaToHeader();
  }
}
